// Options TUS pour tenir sur mobile
//
// Réglages par défaut de tus-js-client v4, et pourquoi ils cassaient les gros
// uploads (60–165 Mo en 4G) :
//   - `retryDelays` [0, 3s, 5s, 10s, 20s] : ~38 s de tolérance au total. Une
//     coupure réseau un peu longue (tunnel, ascenseur, salle de sport en
//     sous-sol) suffisait à tuer l'envoi.
//   - `defaultOnShouldRetry` refuse de réessayer quand `navigator.onLine` est
//     false — exactement le moment où il faudrait attendre et réessayer.
//   - Aucun réessai sur une erreur 4xx, et le token était figé au démarrage :
//     un JWT expiré pendant un long envoi donnait une erreur définitive.
//
// Ici : une fenêtre de réessai d'environ 7 min (remise à zéro par tus dès qu'un
// morceau passe), un token frais avant chaque requête, et un réessai limité sur
// les erreurs d'authentification.

import * as Sentry from '@sentry/react';

// Remis à zéro par tus-js-client dès que l'offset progresse : ce budget couvre
// une seule coupure, pas la durée totale de l'envoi.
export const RESILIENT_RETRY_DELAYS = [
  0, 1000, 3000, 5000, 10000, 20000, 30000, 30000, 60000, 60000, 60000, 60000, 60000, 60000,
];

// Un 401/403 peut venir d'un token expiré (rafraîchi par onBeforeRequest avant
// le réessai) ou d'un vrai refus de droits : on retente un peu, pas 7 minutes.
const MAX_AUTH_RETRIES = 2;

const AUTH_ERROR_BODY = /jwt|token|expired|unauthori[sz]ed/i;

const readStatus = (err) => {
  try {
    return err?.originalResponse ? err.originalResponse.getStatus() : 0;
  } catch {
    return 0;
  }
};

const readBody = (err) => {
  try {
    return err?.originalResponse ? String(err.originalResponse.getBody() || '') : '';
  } catch {
    return '';
  }
};

const isAuthError = (status, err) =>
  status === 401 || status === 403 || (status === 400 && AUTH_ERROR_BODY.test(readBody(err)));

/**
 * Décide si tus doit retenter après `err` (tentative n° `retryAttempt`, base 0).
 * `authRetries` : erreurs d'authentification déjà retentées pour cet envoi.
 * Exporté pour les tests.
 */
export function shouldRetryUpload(err, retryAttempt, authRetries = retryAttempt) {
  const status = readStatus(err);
  let retry;

  if (status === 0 || status >= 500 || status === 409 || status === 423 || status === 429) {
    // Réseau coupé (même si le navigateur se dit hors ligne), serveur, verrou.
    retry = true;
  } else if (isAuthError(status, err)) {
    retry = authRetries < MAX_AUTH_RETRIES;
  } else {
    // 413 trop gros, 415 type refusé, etc. : réessayer ne changera rien.
    retry = false;
  }

  try {
    Sentry.addBreadcrumb({
      category: 'video_upload',
      level: 'warning',
      message: retry ? 'TUS request failed, retrying' : 'TUS request failed, giving up',
      data: {
        status,
        retryAttempt,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      },
    });
  } catch {
    // ignore
  }

  return retry;
}

/**
 * Options à étaler dans `new tus.Upload(file, { ...resilientTusOptions(getAuthToken, { token, headers }), ... })`.
 * `getAuthToken` (AuthContext) rafraîchit le JWT quand il approche de l'expiration.
 *
 * Le header Authorization est posé UNIQUEMENT dans onBeforeRequest, jamais dans
 * `headers` : tus-js-client applique `headers` puis onBeforeRequest sur le même
 * XMLHttpRequest, et setRequestHeader concatène les valeurs répétées
 * (« Bearer X, Bearer X ») → Supabase Storage répond 400 dès la création de
 * l'upload (incident du 14/09/2026, 21 élèves bloqués). Ne pas remettre
 * `headers: { Authorization }` à côté de ces options.
 */
export function resilientTusOptions(getAuthToken, { token: initialToken = null, headers = {} } = {}) {
  /** @type {Record<string, string>} */
  const staticHeaders = {};
  for (const [name, value] of Object.entries(headers)) {
    if (name.toLowerCase() !== 'authorization') staticHeaders[name] = value;
  }

  // Compté à part : le `retryAttempt` de tus inclut les coupures réseau. Après
  // une mise en veille (plusieurs échecs réseau puis un JWT expiré au réveil),
  // il dépassait déjà MAX_AUTH_RETRIES et l'envoi abandonnait sans retenter
  // avec un token rafraîchi (FRONTEND-7J, 23/10/2026).
  let authRetries = 0;

  return {
    retryDelays: RESILIENT_RETRY_DELAYS,
    onShouldRetry: (err, retryAttempt) => {
      const retry = shouldRetryUpload(err, retryAttempt, authRetries);
      if (isAuthError(readStatus(err), err)) authRetries += 1;
      return retry;
    },
    // Une requête acceptée prouve que le token passe à nouveau.
    onAfterResponse: (_req, res) => {
      if (res.getStatus() < 300) authRetries = 0;
    },
    headers: staticHeaders,
    onBeforeRequest: async (req) => {
      let token = null;
      try {
        token = await getAuthToken();
      } catch {
        // Hors ligne ou refresh raté : on retombe sur le token du démarrage, la
        // requête échouera éventuellement et passera par onShouldRetry.
      }
      token = token || initialToken;
      if (token) req.setHeader('Authorization', `Bearer ${token}`);
    },
  };
}
